import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, ArrowLeft, Activity, AlertTriangle, CheckCircle2, RefreshCw, Trash2, FileSearch, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/admin/health")({
  head: () => ({ meta: [{ title: "Admin · Salud operacional — ZYNTRA" }] }),
  component: AdminHealthPage,
});

interface ReconcileRow {
  checkout_session_id: string;
  ticket_count: number;
  gross_amount: number;
  currency: string;
  has_settlement: boolean;
  first_ticket_at: string;
}

interface EmailFailureRow {
  status: string;
  total: number;
  oldest: string;
  newest: string;
  sample_error: string | null;
}

interface HealthSnapshot {
  open_drift_alerts: number;
  total_drift_amount: number;
  pending_settlements: number;
  pending_settlements_amount: number;
  overdue_settlements: number;
  pending_payouts: number;
  pending_payouts_amount: number;
  pending_resale_tx: number;
  active_resale_listings: number;
  active_holds: number;
  recent_inflight_webhooks: number;
  tickets_last_7d: number;
  resales_last_7d: number;
  settlements_created_last_7d: number;
  failed_emails_24h: number;
  snapshot_at: string;
}

interface LoadMetrics {
  snapshot_at: string;
  active_holds_total: number;
  expired_holds_awaiting_cleanup: number;
  tickets_last_5m: number;
  tickets_last_60m: number;
  holds_created_last_5m: number;
  resales_completed_last_60m: number;
  top_events_by_holds: Array<{
    event_id: string;
    event_title: string;
    active_holds: number;
    held_quantity: number;
  }>;
}

function AdminHealthPage() {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [data, setData] = useState<HealthSnapshot | null>(null);
  const [load, setLoad] = useState<LoadMetrics | null>(null);
  const [reconcile, setReconcile] = useState<ReconcileRow[]>([]);
  const [emailFailures, setEmailFailures] = useState<EmailFailureRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState<string | null>(null);

  async function refresh() {
    setRefreshing(true);
    setCleanResult(null);
    const [{ data: rows }, { data: loadRow }, { data: recon }, { data: emails }] = await Promise.all([
      supabase.rpc("get_admin_health"),
      supabase.rpc("get_load_metrics"),
      supabase.rpc("reconcile_stripe_sessions", { _lookback_hours: 24 }),
      supabase.rpc("get_failed_email_summary", { _lookback_hours: 24 }),
    ]);
    if (rows && rows.length > 0) setData(rows[0] as HealthSnapshot);
    if (loadRow) setLoad(loadRow as unknown as LoadMetrics);
    setReconcile((recon as ReconcileRow[]) ?? []);
    setEmailFailures((emails as EmailFailureRow[]) ?? []);
    setRefreshing(false);
  }

  async function cleanupZombies() {
    if (!confirm("Borrar webhooks abandonados (>10 min)?")) return;
    setCleaning(true);
    const { data: out, error } = await supabase.rpc("cleanup_webhook_zombies", { _older_than_minutes: 10 });
    setCleaning(false);
    if (error) {
      setCleanResult(`Error: ${error.message}`);
      return;
    }
    const row = (out as Array<{ stripe_cleaned: number; onvopay_cleaned: number }>)?.[0];
    setCleanResult(`Limpios: Stripe ${row?.stripe_cleaned ?? 0} · Onvopay ${row?.onvopay_cleaned ?? 0}`);
    await refresh();
  }

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data: roleRow } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!roleRow);
      if (roleRow) await refresh();
      setLoading(false);
    })();
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Acceso restringido</h1>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary hover:underline">
          Volver al dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 pb-20 pt-24 sm:px-6">
      <div className="mb-4 flex items-center justify-between">
        <Link to="/dashboard/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Configuración financiera
        </Link>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-secondary px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refrescar
        </button>
      </div>

      <div className="mb-6 flex items-center gap-2">
        <Activity className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-bold text-foreground">Salud operacional</h1>
      </div>

      {!data ? (
        <p className="text-sm text-muted-foreground">Sin datos disponibles.</p>
      ) : (
        <>
          <SectionHeader title="Alertas críticas" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Card
              label="Drift de wallet abierto"
              value={data.open_drift_alerts}
              suffix={data.total_drift_amount > 0 ? `≈ ₡${Math.round(Number(data.total_drift_amount)).toLocaleString("es-CR")}` : undefined}
              tone={Number(data.open_drift_alerts) > 0 ? "danger" : "ok"}
            />
            <Card
              label="Settlements vencidos (>7d)"
              value={data.overdue_settlements}
              tone={Number(data.overdue_settlements) > 0 ? "warn" : "ok"}
              hint="Auto-release diario los procesa. Si crece, revisar cron."
            />
            <Card
              label="Emails fallidos (24h)"
              value={data.failed_emails_24h}
              tone={Number(data.failed_emails_24h) > 5 ? "warn" : "ok"}
            />
          </div>

          <SectionHeader title="Pendientes financieros" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Card
              label="Settlements pendientes"
              value={data.pending_settlements}
              suffix={`₡${Math.round(Number(data.pending_settlements_amount)).toLocaleString("es-CR")}`}
              link={{ to: "/admin/settlements", label: "Gestionar →" }}
            />
            <Card
              label="Payouts pendientes"
              value={data.pending_payouts}
              suffix={`₡${Math.round(Number(data.pending_payouts_amount)).toLocaleString("es-CR")}`}
              link={{ to: "/admin/payouts", label: "Gestionar →" }}
            />
            <Card
              label="Reventas en curso"
              value={data.pending_resale_tx}
              hint="Pago iniciado, aún no completado."
            />
          </div>

          <SectionHeader title="Inventario y tráfico" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card label="Holds activos" value={data.active_holds} />
            <Card label="Listings reventa" value={data.active_resale_listings} />
            <Card label="Webhooks en vuelo" value={data.recent_inflight_webhooks} hint="<10min" />
            <Card label="Tickets últimos 7d" value={data.tickets_last_7d} />
            <Card label="Reventas completadas 7d" value={data.resales_last_7d} />
            <Card label="Settlements creados 7d" value={data.settlements_created_last_7d} />
          </div>

          <p className="mt-8 text-[11px] text-muted-foreground">
            Snapshot: {new Date(data.snapshot_at).toLocaleString("es-CR")}
          </p>
        </>
      )}

      {load && (
        <>
          <SectionHeader title="Carga en tiempo real" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card label="Holds activos (total)" value={load.active_holds_total} />
            <Card
              label="Holds vencidos sin limpiar"
              value={load.expired_holds_awaiting_cleanup}
              tone={load.expired_holds_awaiting_cleanup > 100 ? "warn" : "ok"}
              hint="Cron expira holds; si crece, revisar."
            />
            <Card label="Tickets últimos 5 min" value={load.tickets_last_5m} />
            <Card label="Tickets últimos 60 min" value={load.tickets_last_60m} />
            <Card label="Holds creados últimos 5 min" value={load.holds_created_last_5m} />
            <Card label="Reventas completadas 60 min" value={load.resales_completed_last_60m} />
          </div>

          {load.top_events_by_holds.length > 0 && (
            <>
              <SectionHeader title="Top eventos por holds activos" />
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-[11px] uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Evento</th>
                      <th className="px-3 py-2 text-right font-semibold">Holds</th>
                      <th className="px-3 py-2 text-right font-semibold">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {load.top_events_by_holds.map((ev) => (
                      <tr key={ev.event_id} className="border-t border-border">
                        <td className="px-3 py-2 text-foreground">{ev.event_title}</td>
                        <td className="px-3 py-2 text-right font-mono">{ev.active_holds}</td>
                        <td className="px-3 py-2 text-right font-mono">{ev.held_quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-6">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Webhooks abandonados</h2>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Borra entradas en <code>webhook_inflight</code> y <code>onvopay_webhook_inflight</code> con &gt; 10 min sin completarse (worker muerto / red).
          </p>
        </div>
        <button
          onClick={cleanupZombies}
          disabled={cleaning}
          className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/20 disabled:opacity-50"
        >
          <Trash2 className={`h-3.5 w-3.5 ${cleaning ? "animate-pulse" : ""}`} />
          Limpiar zombies (&gt;10min)
        </button>
      </div>
      {cleanResult && (
        <p className="mt-2 text-[11px] text-muted-foreground">{cleanResult}</p>
      )}

      <SectionHeader title={`Reconciliación Stripe (últimas 24 h)`} />
      <div className="mb-2 flex items-center gap-2 text-[11px] text-muted-foreground">
        <FileSearch className="h-3.5 w-3.5" />
        Sesiones de cobro con tickets emitidos. Verde = settlement creado; rojo = falta settlement (revisar webhook).
      </div>
      {reconcile.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin actividad Stripe en el período.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-xs">
            <thead className="bg-secondary text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Sesión</th>
                <th className="px-3 py-2 text-right font-semibold">Tickets</th>
                <th className="px-3 py-2 text-right font-semibold">Gross</th>
                <th className="px-3 py-2 text-center font-semibold">Settlement</th>
                <th className="px-3 py-2 text-left font-semibold">Cuándo</th>
              </tr>
            </thead>
            <tbody>
              {reconcile.map((r) => (
                <tr key={r.checkout_session_id} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono text-[10px] text-foreground">{r.checkout_session_id.slice(0, 28)}…</td>
                  <td className="px-3 py-1.5 text-right font-mono">{r.ticket_count}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{r.currency} {Math.round(Number(r.gross_amount)).toLocaleString("es-CR")}</td>
                  <td className="px-3 py-1.5 text-center">
                    {r.has_settlement ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-semibold text-success">OK</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive">FALTA</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">{new Date(r.first_ticket_at).toLocaleString("es-CR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SectionHeader title="Emails problemáticos (24 h)" />
      <div className="mb-2 flex items-center gap-2 text-[11px] text-muted-foreground">
        <Mail className="h-3.5 w-3.5" />
        Resumen agrupado por estado: failed (intento fallido), dlq (excedió reintentos), suppressed (rebote/queja).
      </div>
      {emailFailures.length === 0 ? (
        <p className="text-xs text-success">✓ Sin fallos en el período.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          {emailFailures.map((e) => (
            <Card
              key={e.status}
              label={e.status}
              value={e.total}
              tone={e.status === "dlq" ? "danger" : "warn"}
              hint={e.sample_error ? `Último: ${e.sample_error.slice(0, 80)}` : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return <h2 className="mb-3 mt-8 text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</h2>;
}

function Card({
  label,
  value,
  suffix,
  hint,
  tone,
  link,
}: {
  label: string;
  value: number | string;
  suffix?: string;
  hint?: string;
  tone?: "ok" | "warn" | "danger";
  link?: { to: string; label: string };
}) {
  const toneClass =
    tone === "danger"
      ? "border-destructive/40 bg-destructive/5"
      : tone === "warn"
        ? "border-warning/40 bg-warning/5"
        : "border-border bg-card";
  const Icon = tone === "danger" ? AlertTriangle : tone === "warn" ? AlertTriangle : CheckCircle2;
  const iconColor = tone === "danger" ? "text-destructive" : tone === "warn" ? "text-warning" : "text-success";
  return (
    <div className={`rounded-xl border p-4 ${toneClass}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase text-muted-foreground">{label}</p>
        {tone && <Icon className={`h-4 w-4 ${iconColor}`} />}
      </div>
      <p className="mt-2 text-2xl font-bold text-foreground">{value}</p>
      {suffix && <p className="mt-0.5 text-xs text-muted-foreground">{suffix}</p>}
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
      {link && (
        <Link to={link.to} className="mt-2 inline-block text-[11px] font-semibold text-primary hover:underline">
          {link.label}
        </Link>
      )}
    </div>
  );
}
